import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { formFields, screenInfo } from "./AccumulationCodeConfig";
const { ACCUMULATION_CODE_LIST_CODES } = ROUTE_PATHS;
const AccumulationCodeForm = ({
  mode,
  selectMode,
  selectedSchedule,
  hideModal,
}) => {
  const location = useLocation();

  const [stateVal, setStateVal] = useState(location.state || []);
  const { previousState, currentState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);

    return { previousState, currentState };
  }, [stateVal]);
  const {
    selectedCodes,
    previousScreen,
    initialIndex,
    initialExternalMode,
    initialLookupType,
  } = useMemo(() => {
    let {
      selectedCodes = [],
      previousScreen,
      currentIndex: initialIndex = 0,
      lookupType: initialLookupType,
      externalMode: initialExternalMode = false,
    } = currentState;
    return {
      selectedCodes,
      previousScreen,
      initialIndex,
      initialExternalMode,
      initialLookupType,
    };
  }, [currentState]);

  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState([]);
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [externalMode, setExternalMode] = useState(initialExternalMode);
  const toast = useToast();
  const { axios_helper_no_header } = useAxios();

  const lastScheduleName =
    selectedCodes[currentIndex]?.acmAccumulationCode || "";
  const updatedParamsData = {
    ...currentState.paramsData,
    acmAccumulationCode: lastScheduleName,
    hiddenRecordIndicator: "Active",
  };

  const handleBack = useCallback(() => {
    if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      console.log("back messages", message);
      window.parent.postMessage(message, "*");
    } else if (selectMode) {
      hideModal();
    } else if (currentIndex < selectedCodes.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      const latestPreviousState = previousState.pop() ?? {};
      const { paramsData = {} } = latestPreviousState;
      const lastScheduleName =
        selectedCodes[currentIndex]?.acmAccumulationCode || "";
      const updatedLatestPreviousState = {
        ...latestPreviousState,
        paramsData: {
          ...paramsData,
          acmAccumulationCode: lastScheduleName,
          hiddenRecordIndicator: "Active",
        },
      };
      navigateToPage(previousScreen, [
        ...previousState,
        updatedLatestPreviousState,
      ]);
    }
  }, [currentIndex, selectedCodes]);

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/acmAccumulationCode";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/acmAccumulationCode";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        const latestCurrentState = {
          ...currentState,
          selectedCodes: [{ ...responseData }],
          currentIndex: 0,
        };
        setStateVal([...previousState, latestCurrentState]);
        // const latestPreviousState = previousState.pop() ?? {};
        navigateToPage(ROUTE_PATHS.ACCUMULATION_CODE_ADD, [
          ...previousState,
          latestCurrentState,
        ]);
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};
    if (selectMode) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, handleBack]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const code = selectMode
        ? selectedSchedule[0]?.acmAccumulationCode
        : selectedCodes[currentIndex]?.acmAccumulationCode;
      const url = isAddMode(currentMode)
        ? `api/acmAccumulationCode/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`
        : `api/acmAccumulationCode/${encodeURIComponent(code)}`;
      const { responseData } = await axios_helper_no_header("GET", url);
      setInitialFormData(responseData);
    };

    if (selectedCodes && Object.keys(selectedCodes).length) {
      fetchInitialData();
    }
  }, [mode, currentIndex, selectedCodes]);

  const handleMessage = (event) => {
    const { type, data } = event.data;
    if (type === "set-params") {
      setStateVal(data);
      setExternalMode(true);
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
        />
      </div>
    </div>
  );
};

export default AccumulationCodeForm;
